export type Tab =
  | 'scaling'
  | 'growth'
  | 'browser-ladder'
  | 'web-work-mix'
  | 'ops-capacity'
  | 'users'
  | 'launches'
  | 'promotions'
  | 'overview'
  | 'onboarding'
  | 'acquisition'
  | 'charts'
  | 'cohorts'
  | 'outcomes'
  | 'costs'
  | 'email-routing'
  | 'ai-calls'
  | 'browser-mfa'
  | 'user-lookup'
  | 'ops-workers'
  | 'campaign-codes'
  | 'feature-flags'
  | 'user-experience'
  | 'payments';

const ALL_TABS: readonly Tab[] = [
  'scaling',
  'growth',
  'browser-ladder',
  'web-work-mix',
  'ops-capacity',
  'users',
  'launches',
  'promotions',
  'overview',
  'onboarding',
  'acquisition',
  'charts',
  'cohorts',
  'outcomes',
  'costs',
  'email-routing',
  'ai-calls',
  'browser-mfa',
  'user-lookup',
  'ops-workers',
  'campaign-codes',
  'feature-flags',
  'user-experience',
  'payments',
];

const isTab = (v: string | null): v is Tab =>
  v != null && (ALL_TABS as readonly string[]).includes(v);

/**
 * Top-level categories and their pages. A page's URL is
 * /platform-stats/<category>/<page>, so any page can be linked to.
 */
export const TAB_GROUPS: ReadonlyArray<{
  key: string;
  label: string;
  tabs: ReadonlyArray<{ key: Tab; label: string }>;
}> = [
  {
    key: 'headlines',
    label: 'Headlines',
    tabs: [
      { key: 'scaling', label: 'Scaling' },
      { key: 'overview', label: 'Overview' },
      { key: 'growth', label: 'Growth' },
    ],
  },
  {
    key: 'engagement',
    label: 'Engagement',
    tabs: [
      { key: 'onboarding', label: 'Onboarding' },
      { key: 'acquisition', label: 'Acquisition' },
      { key: 'user-experience', label: 'User Experience' },
      { key: 'launches', label: 'Launches' },
      { key: 'promotions', label: 'Promotions' },
    ],
  },
  {
    key: 'operations',
    label: 'Operations',
    tabs: [
      { key: 'ops-capacity', label: 'Ops Capacity' },
      { key: 'web-work-mix', label: 'Web Work Mix' },
      { key: 'browser-ladder', label: 'Browser Ladder' },
    ],
  },
  {
    key: 'costs-payments',
    label: 'Costs & Payments',
    tabs: [
      { key: 'costs', label: 'Costs' },
      { key: 'payments', label: 'Payments' },
    ],
  },
  {
    key: 'admin',
    label: 'Admin',
    tabs: [
      { key: 'users', label: 'Users' },
      { key: 'user-lookup', label: 'User Lookup' },
      { key: 'ops-workers', label: 'Ops Workers' },
      { key: 'campaign-codes', label: 'Campaign Codes' },
      { key: 'feature-flags', label: 'Feature Flags' },
    ],
  },
  {
    key: 'legacy',
    label: 'Legacy',
    tabs: [
      { key: 'charts', label: 'Charts' },
      { key: 'cohorts', label: 'User Activity' },
      { key: 'outcomes', label: 'Outcomes' },
      { key: 'email-routing', label: 'Email Routing' },
      { key: 'ai-calls', label: 'AI Calls' },
      { key: 'browser-mfa', label: 'Browser & MFA' },
    ],
  },
];

export const groupOf = (tab: Tab) =>
  TAB_GROUPS.find((g) => g.tabs.some((t) => t.key === tab)) ?? TAB_GROUPS[0];

export const platformStatsPath = (tab: Tab) =>
  `/platform-stats/${groupOf(tab).key}/${tab}`;

/**
 * Resolves the page from /platform-stats/<category>/<page>. A category alone
 * opens its first page; old `?tab=` links still resolve to their page.
 */
export const resolveTab = (pathname: string, legacyTab: string | null): Tab => {
  if (isTab(legacyTab)) return legacyTab;
  const [groupKey, page] = pathname
    .replace(/^\/platform-stats\/?/, '')
    .split('/');
  const group = TAB_GROUPS.find((g) => g.key === groupKey);
  if (!group) return 'scaling';
  return group.tabs.find((t) => t.key === page)?.key ?? group.tabs[0].key;
};
